import { type JSX, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Icon } from '@/components/icons';
import { Button, Input, Spinner } from '@/components/ui';
import { api, ApiError } from '@/lib/api';

const schema = z.object({ email: z.string().email() });
type FormValues = z.infer<typeof schema>;

/**
 * Forgot-password entry page. The form posts the email to
 * /v1/auth/forgot-password and renders a generic success message
 * regardless of whether the account exists (anti-enumeration on the
 * backend; mirroring the same blank-wall response in the UI).
 */
export default function ForgotPasswordPage(): JSX.Element {
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<FormValues>({ defaultValues: { email: '' } });

  const onSubmit = handleSubmit(async (values) => {
    setErrorMsg(null);
    const parsed = schema.safeParse(values);
    if (!parsed.success) {
      setError('email', { message: parsed.error.issues[0]?.message ?? 'Invalid email' });
      return;
    }
    try {
      await api.post<{ message: string }>('/v1/auth/forgot-password', { email: parsed.data.email });
      setSubmitted(true);
    } catch (err) {
      // The api should never bubble validation errors back here (the
      // server normalizes them into the same 202). A 5xx means
      // misconfigured email/PUBLIC_BASE_URL on the server — surface
      // generically.
      if (err instanceof ApiError) {
        setErrorMsg('Something went wrong. Please try again in a moment.');
      } else {
        setErrorMsg('Network error. Check your connection and retry.');
      }
    }
  });

  return (
    <div className="w-full min-h-screen grid place-items-center bg-paper px-6">
      <div className="w-full max-w-[400px]">
        <div className="mb-6">
          <Link to="/login" className="inline-flex items-center gap-1.5 text-[13px] text-text-3 hover:text-text">
            <Icon.chevronLeft width="14" height="14" />
            Back to sign in
          </Link>
        </div>

        <h1 className="display text-[28px] font-semibold tracking-[-0.02em] text-text leading-[1.15] m-0">
          Reset your password
        </h1>
        <p className="text-[14.5px] text-text-3 mt-2.5 mb-7 leading-relaxed">
          Enter the email tied to your account. If we find a match, we'll send a link to reset
          your password.
        </p>

        {submitted ? (
          <div
            role="status"
            className="px-4 py-4 bg-success-bg border border-success-bd rounded-md text-[14px] text-success leading-relaxed"
          >
            <strong className="block mb-1">Check your inbox</strong>
            If an account with that email exists, a password reset link has been sent. The link
            expires in one hour.
          </div>
        ) : (
          <form onSubmit={onSubmit} noValidate>
            {errorMsg && (
              <div
                role="alert"
                className="flex gap-2.5 items-start px-3.5 py-3 bg-danger-bg border border-danger-bd rounded-md mb-5"
              >
                <Icon.warn width="18" height="18" className="text-danger flex-shrink-0 mt-px" />
                <div className="text-[13px] text-danger leading-relaxed">{errorMsg}</div>
              </div>
            )}

            <div className="mb-5">
              <label
                htmlFor="forgot-email"
                className="block text-[12px] font-medium text-text-2 mb-1.5"
              >
                Email
              </label>
              <Input
                id="forgot-email"
                type="email"
                autoComplete="email"
                placeholder="you@company.com"
                disabled={isSubmitting}
                error={Boolean(errors.email)}
                {...register('email')}
              />
              {errors.email && (
                <p className="text-[12px] text-danger mt-1.5">{errors.email.message}</p>
              )}
            </div>

            <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner />
                  <span>Sending…</span>
                </>
              ) : (
                <span>Send reset link</span>
              )}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
